import { watch } from 'vue'

const PAGE_CONCURRENCY = 10

/** Load the first page for pagination headers, then fetch the rest in order. */
async function loadPages(loadPage, progress) {
  const { headers, data } = await loadPage()
  const pagesCount = Number(headers['x-pagination-page-count']) || 1
  const currentPage = Number(headers['x-pagination-current-page']) || 1
  const total = Number(headers['x-pagination-total-count'])
  progress.totalPages = Math.max(1, pagesCount - currentPage + 1)
  progress.total = Number.isFinite(total)
    ? total
    : progress.totalPages * data.length
  const pages = [data]
  let nextPage = currentPage + 1

  const loadNextPages = async () => {
    while (nextPage <= pagesCount) {
      const page = nextPage++
      const response = await loadPage(page)
      pages[page - currentPage] = response.data
    }
  }
  await Promise.all(
    Array.from(
      {
        length: Math.min(PAGE_CONCURRENCY, pagesCount - currentPage),
      },
      loadNextPages,
    ),
  )
  return pages.flat()
}

/**
 * Create a paginated list loader for a store with loadProgress state.
 * Concurrent calls share the load; resetting the store cancels its requests.
 *
 * @param {Function} request - API list operation accepting params and signal
 * @param {object} [params] - Entity filters and expansions
 * @returns {Function} Load a store list and save it after all pages succeed
 */
export function createListLoader(request, params = {}) {
  const activeLoads = new WeakMap()

  return function loadList(store, save) {
    // Reuse the current load; cancel requests left over from a store reset.
    const activeLoad = activeLoads.get(store)
    if (activeLoad && activeLoad.progress === store.loadProgress) {
      return activeLoad.promise
    }
    activeLoad?.controller.abort()

    // Keep progress in the store so a reset also invalidates this load.
    store.loadProgress = {
      loaded: 0,
      total: 0,
      completedPages: 0,
      totalPages: 0,
      startedAt: Date.now(),
      lastProgressAt: Date.now(),
    }
    const progress = store.loadProgress
    const controller = new AbortController()
    const stopWatching = watch(
      () => store.loadProgress,
      (current) => {
        if (current !== progress) controller.abort()
      },
      { flush: 'sync' },
    )
    const isCurrentLoad = () => store.loadProgress === progress

    // Update progress only for responses belonging to the current load.
    const loadPage = async (page) => {
      const response = await request(
        { ...params, page },
        { signal: controller.signal },
      )
      // A logout resets the store; old responses must not restore its data.
      if (!isCurrentLoad()) {
        controller.abort()
        throw new Error('List loading was cancelled')
      }
      progress.loaded += response.data.length
      progress.completedPages++
      progress.lastProgressAt = Date.now()
      return response
    }

    // Publish the full list atomically and release resources on every outcome.
    async function run() {
      try {
        const items = await loadPages(loadPage, progress)
        if (isCurrentLoad()) {
          save(items)
          progress.total = items.length
        }
      } catch (error) {
        controller.abort()
        throw error
      } finally {
        stopWatching()
        if (activeLoads.get(store)?.progress === progress) {
          activeLoads.delete(store)
        }
      }
    }

    const promise = run()
    activeLoads.set(store, { promise, progress, controller })
    return promise
  }
}
